import { useState, useCallback, useEffect, useRef } from "react";
//import { useLocation } from "react-router-dom";
import SectionTitle from "../common/SectionTitle";
import { MapPin, Phone } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import ContactInfo from "../components/ContactInfo";
import { siteData } from "../data/siteData";
import SeoHelper from '../common/SeoHelper';
import ReCAPTCHA from "react-google-recaptcha";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";


const Contact = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const recaptchaRef = useRef(null);

  const [captchaToken, setCaptchaToken] = useState("");
  const [emailTemplate, setEmailTemplate] = useState("");
  const [apiEndpoints, setApiEndpoints] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    fullName: "",
    mobile: "",
    email: "",
    villaType: "",
    message: "",
  });
  useEffect(() => {
  fetch("/template/email.txt")
    .then((res) => res.text())
    .then((data) => setEmailTemplate(data))
    .catch((err) => {
      console.error("Template load error:", err);
      toast.error("Failed to load email template");
    });

  fetch("/JSON/apiendpoints.json")
    .then((res) => res.json())
    .then((data) => setApiEndpoints(data))
    .catch((err) => {
      console.error("API config error:", err);
      toast.error("Failed to load API configuration");
    });
}, []);

  useEffect(() => {
    if (location.state?.villaType) {
      setFormData((prev) => ({
        ...prev,
        villaType: location.state.villaType,
      }));
    }
  }, [location.state]);

  const handleVerify = useCallback((token) => {
    setCaptchaToken(token);
  }, []);

  const resetCaptcha = () => {
    setCaptchaToken("");
    recaptchaRef.current?.reset();
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  const handleSubmit = async (e) => {
  e.preventDefault();
  if (isSubmitting) return;

  if (!captchaToken) {
    toast.error("Please verify that you are not a robot.");
    return;
  }

  if (!emailTemplate) {
    toast.error("Email template not loaded yet.");
    return;
  }

  if (!apiEndpoints?.enquiryUrl) {
    toast.error("API endpoint not ready. Please try again.");
    return;
  }

  setIsSubmitting(true);

  try {
    let filledTemplate = emailTemplate;
    Object.keys(formData).forEach((key) => {
      filledTemplate = filledTemplate.replace(
        new RegExp(`{{${key}}}`, "g"),
        formData[key] || ""
      );
    });

    const payload = {
      FromMail: formData.email,
      EntityName: "florettown",
      EmailSubject: "New Enquiry - Floret Town",
      EmailBody: filledTemplate,
      FullName: formData.fullName,
      Mobile: formData.mobile,
      VillaType: formData.villaType,
      Message: formData.message,
      CaptchaToken: captchaToken,
    };

    const response = await fetch(apiEndpoints.enquiryUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const err = await response.text();
      throw new Error(err || "Submission failed");
    }

    toast.success("Enquiry has been submitted successfully");
    sessionStorage.setItem("formSubmitted", "true");

    setFormData({
      fullName: "",
      mobile: "",
      email: "",
      villaType: "",
      message: "",
    });

    resetCaptcha();
    navigate("/thank-you");
  } catch (error) {
    console.error("Submit error:", error);
    toast.error(error.message || "Something went wrong. Please try again later.");
  } finally {
    setIsSubmitting(false);
  }
};


  return (
    <>
    <SeoHelper/>
    <section
      id="contact"
      className="pt-24 pb-20 bg-gradient-to-br from-emerald-50 to-teal-50"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionTitle
          title="Get in Touch"
          subtitle="We're here to help you find your dream home"
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 mt-8 sm:mt-10 gap-8 sm:gap-12">
          {/* Left */}
          <div className="space-y-8">
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <h3 className="text-2xl font-bold mb-6">Contact Information</h3>

              <ContactInfo icon={Phone} title="Call Us">
                <a
                  href={`tel:${siteData.contact.phone}`}
                  className="text-emerald-600 block"
                >
                  {siteData.contact.phone}
                </a>
              </ContactInfo>

              <ContactInfo icon={FaWhatsapp} title="Chat Now">
                <a
                  href={`https://wa.me/${siteData.contact.whatsapp.replace(/\D/g, "")}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-emerald-600"
                >
                  WhatsApp
                </a>
              </ContactInfo>

              <ContactInfo icon={MapPin} title="Site Address">
                <p >{siteData.contact.siteAddress}</p>
              </ContactInfo>
            </div>
          </div>

          {/* Right */}
          <div className="bg-white p-8 rounded-2xl shadow-lg">
            <h3 className="text-2xl font-bold mb-6">Enquiry Form</h3>

            <form onSubmit={handleSubmit} className="space-y-6">
              <input
                type="text"
                name="fullName"
                placeholder="Full Name"
                value={formData.fullName}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border rounded-lg"
              />

              <input
                type="tel"
                name="mobile"
                placeholder="Mobile Number"
                value={formData.mobile}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border rounded-lg"
              />

              <input
                type="email"
                name="email"
                placeholder="Email Address"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border rounded-lg"
              />

              <select
                name="villaType"
                value={formData.villaType}
                onChange={handleChange}
                className="w-full px-4 py-3 border rounded-lg"
              >
                <option value="">Select Villa Type</option>
                {siteData.villaTypes.map((v, i) => (
                  <option key={i} value={v}>
                    {v}
                  </option>
                ))}
              </select>

              <textarea
                name="message"
                rows="4"
                placeholder="Message / Requirement"
                value={formData.message}
                onChange={handleChange}
                className="w-full px-4 py-3 border rounded-lg"
              />
             <div className="overflow-hidden scale-90 sm:scale-100 origin-left">
              <ReCAPTCHA
                sitekey="6LeaUlAsAAAAAIqBpQQxOmwF7d3nb2MnolfTulTI"
                ref={recaptchaRef}
                onChange={handleVerify}
              />
              </div>

              <button
                type="submit"
                disabled={isSubmitting || !captchaToken}
                className="w-full py-3 sm:py-4 text-base sm:text-lg bg-emerald-600 text-white rounded-lg"
              >
                {isSubmitting ? "Submitting..." : "Submit Enquiry"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
    </>
  );
};

export default Contact;
